import { MiraSection, MiraSectionDocument } from "@/data/models/Mira";
import { getClientId, getMemberClientIds, getMemberClientsInformationOfGroup } from "@/data/repository/ClientRepository";
import { companyViewOf, companyViewRoster } from "@/utils/MiraCompanyView";

/** What can stand between the shared part and the rest of a name ("svt Schadensanierung GmbH - SAN_Kiel"). */
const LEADING_SEPARATORS = /^[\s\-–—:·|,/]+/;

/**
 * The names of `ids` without the words every one of them starts with (decision D3, Taimoor, 2026-11-03):
 * a group's Mandanten often repeat the group's name ("svt Schadensanierung GmbH - SAN_Kiel", "… GmbH
 * Zentrallager"), which the page already names, so Mira shows "SAN_Kiel" and "Zentrallager". Cut at a
 * word boundary, then a leading separator trimmed. Nothing changes with fewer than two names or nothing
 * shared, and a name that would be left empty keeps its full form. Other names in `names` are returned as
 * they are; the stored names are never touched.
 */
export function withoutSharedPrefix(names: Record<string, string>, ids: string[]): Record<string, string> {
  const shown = ids.filter((id) => typeof names[id] === "string");
  if (shown.length < 2) {
    return names;
  }
  const words = shown.map((id) => names[id].trim().split(/\s+/));
  let shared = 0;
  while (words.every((nameWords) => nameWords.length > shared && nameWords[shared] === words[0][shared])) {
    shared++;
  }
  if (shared === 0) {
    return names;
  }
  const result = { ...names };
  shown.forEach((id, index) => {
    const rest = words[index].slice(shared).join(" ").replace(LEADING_SEPARATORS, "");
    if (rest) {
      result[id] = rest;
    }
  });
  return result;
}

/** The roster with the Mandanten's shared name part left out; the master, no Mandant to compare, takes no part. */
function withShortNames(roster: { ids: string[]; names: Record<string, string> }): { ids: string[]; names: Record<string, string> } {
  const masterId = getClientId();
  return { ids: roster.ids, names: withoutSharedPrefix(roster.names, roster.ids.filter((id) => id !== masterId)) };
}

/**
 * The Mandanten a group master compares, by id, with their names: a company's viewing client (#2610112
 * X6e) takes them from the `companyView` IRES Cloud wrote into its documents, since it may not read the
 * Mandanten; a real master from its own `memberClientIds` (catalog S16.2), named from the group registry.
 * Shared by the screens and the overview's "Vergleiche verwalten" (#2610137, 2026-11-02).
 */
export async function loadMiraMemberRoster(
  sections: Partial<Record<MiraSection, MiraSectionDocument>>
): Promise<{ ids: string[]; names: Record<string, string> }> {
  const companyView = companyViewOf(sections);
  if (companyView) {
    return withShortNames(companyViewRoster(companyView));
  }
  // The registry only supplies names: it still lists removed members, because its writes merge and a
  // removal never clears an entry.
  const ids = getMemberClientIds().filter((id) => !!id);
  const info = await getMemberClientsInformationOfGroup();
  const names: Record<string, string> = {};
  info.forEach((client) => {
    const id = client.clientId;
    if (id && ids.includes(id)) {
      names[id] = (client.shortName || client.companyName || client.clientName || id).trim();
    }
  });
  // A member the registry does not name still counts, under its id, rather than silently vanishing.
  ids.forEach((id) => {
    if (!names[id]) names[id] = id;
  });
  return withShortNames({ ids, names });
}
